import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import upload from '../services/upload';
import auth from '../services/auth';
import './PostEditor.css';

const EditPostPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState({
        title: '',
        content: '',
        is_published: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!auth.isAuthenticated()) {
            navigate('/admin/login');
            return;
        }
        
        const fetchPost = async () => {
            try {
                const response = await api.getPost(slug);
                setPost(response.data);
            } catch (err) {
                setError('Failed to load post');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug, navigate]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const result = await upload.uploadImage(file);
            // Insert image markdown at end of content
            const imageMarkdown = `\n![Image](${result.url})\n`;
            setPost({...post, content: post.content + imageMarkdown});
        } catch (err) {
            alert('Failed to upload image');
            console.error('Error:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        
        try {
            await api.updatePost(slug, post);
            navigate('/admin/dashboard');
        } catch (err) {
            setError('Failed to update post');
            console.error('Error:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="editor-container">Loading...</div>;

    return (
        <div className="editor-container">
            <header className="editor-header">
                <h1><Link to="/">Sercihan's</Link></h1>
            </header>
            
            <div className="editor-content">
                <h2>Edit Post</h2>
                
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="editor-form">
                    <div className="form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={post.title}
                            onChange={(e) => setPost({...post, title: e.target.value})}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <div className="label-with-button">
                            <label>Content</label>
                            <label className="upload-button" disabled={uploading}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                />
                                {uploading ? 'Uploading...' : '+ Add Image'}
                            </label>
                        </div>
                        <textarea
                            rows="20"
                            value={post.content}
                            onChange={(e) => setPost({...post, content: e.target.value})}
                            required
                        />
                    </div>
                    
                    <div className="form-checkbox">
                        <input
                            type="checkbox"
                            checked={post.is_published}
                            onChange={(e) => setPost({...post, is_published: e.target.checked})}
                            id="publishCheck"
                        />
                        <label htmlFor="publishCheck">
                            Published
                        </label>
                    </div>
                    
                    <div className="form-actions">
                        <button type="submit" disabled={saving || uploading} className="submit-button">
                            {saving ? 'Updating...' : 'Update Post'}
                        </button>
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => navigate('/admin/dashboard')}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPostPage;
